import React, { useEffect, useRef, useState } from 'react';
import BixButton from '$/components/global/bixButton';
import { WmtTaskNote } from './taskNotes.types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Avatar colour palette — distinct enough to tell authors apart at a glance. */
const AVATAR_COLORS = [
  '#6366f1', // indigo
  '#7c3aed', // violet
  '#2563eb', // blue
  '#0d9488', // teal
  '#16a34a', // green
  '#ea580c', // orange
  '#db2777', // pink
  '#0891b2', // cyan
];

/** Returns a stable colour for an author based on their name. */
function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/**
 * Formats a timestamp as a human-friendly relative string.
 * Within the last hour: "Xm ago". Same day: "Xh ago". Older: locale date.
 */
function formatNoteTimestamp(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

interface TaskNotesSectionProps {
  /** Full notes conversation history for this task. */
  notes: WmtTaskNote[];
  /** The current in-progress note text (controlled by the parent modal). */
  draftNote: string;
  /** Called when the user types in the compose textarea. */
  onDraftChange: (value: string) => void;
  /**
   * Called when the user sends a note standalone (without another action).
   * The parent is responsible for calling `addNote` and clearing the draft.
   */
  onSendNote: () => void;
  /** Disables the compose area (e.g., while the modal is submitting). */
  disabled?: boolean;
}

/**
 * Chat-style Notes section for WMT task modals.
 *
 * Purely presentational — all state management lives in the parent modal.
 * Designed to be reusable in the notification centre (no WMT-specific imports).
 */
const TaskNotesSection: React.FC<TaskNotesSectionProps> = ({
  notes,
  draftNote,
  onDraftChange,
  onSendNote,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const notesListEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the newest note when the list grows
  useEffect(() => {
    if (isExpanded && notesListEndRef.current) {
      notesListEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [notes.length, isExpanded]);

  const canSend = draftNote.trim().length > 0 && !disabled;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSend) {
      e.preventDefault();
      onSendNote();
    }
  };

  const toggleExpanded = () => setIsExpanded(prev => !prev);

  return (
    <div className="create-task-modal__notes-section">
      {/* ── Section header (collapsible) ── */}
      <div
        className="create-task-modal__notes-header"
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={toggleExpanded}
        onKeyDown={e => e.key === 'Enter' && toggleExpanded()}>
        <span className="create-task-modal__notes-title">
          Notes
          {notes.length > 0 && (
            <span className="create-task-modal__notes-badge" aria-label={`${notes.length} notes`}>
              {notes.length}
            </span>
          )}
        </span>
        <span className={`create-task-modal__notes-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true">
          ▾
        </span>
      </div>

      {/* ── Expanded body: history + compose ── */}
      {isExpanded && (
        <div className="create-task-modal__notes-body">
          {/* Conversation history */}
          {notes.length > 0 ? (
            <div className="create-task-modal__notes-list" role="log" aria-live="polite" aria-label="Notes history">
              {notes.map(note => (
                <div key={note.id} className="create-task-modal__note-item">
                  <div
                    className="create-task-modal__note-avatar"
                    style={{ backgroundColor: getAvatarColor(note.authorName) }}
                    aria-hidden="true">
                    {note.authorInitials}
                  </div>
                  <div className="create-task-modal__note-content">
                    <div className="create-task-modal__note-meta">
                      <span className="create-task-modal__note-author">{note.authorName}</span>
                      <span
                        className="create-task-modal__note-timestamp"
                        title={note.timestamp.toLocaleString('en-US')}>
                        {formatNoteTimestamp(note.timestamp)}
                      </span>
                    </div>
                    <p className="create-task-modal__note-text">{note.text}</p>
                  </div>
                </div>
              ))}
              {/* Scroll anchor */}
              <div ref={notesListEndRef} />
            </div>
          ) : (
            <p className="create-task-modal__notes-empty">No notes yet — be the first to add one.</p>
          )}

          {/* Compose area */}
          <div className="create-task-modal__note-compose">
            <textarea
              className="create-task-modal__note-textarea"
              placeholder="Add a note… (Ctrl+Enter to send)"
              value={draftNote}
              onChange={e => onDraftChange(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              rows={3}
              aria-label="New note text"
            />
            <div className="create-task-modal__note-compose-actions">
              <span className="create-task-modal__note-hint">
                Ctrl+Enter to send · Included when you take an action
              </span>
              <BixButton onClick={onSendNote} disabled={!canSend} useSmall>
                Send Note
              </BixButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskNotesSection;
